import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, Grid,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Alert, CircularProgress, Avatar, Tooltip, IconButton,
  InputAdornment, Divider, FormControlLabel, Switch, List, ListItem,
  ListItemText, Collapse,
} from '@mui/material';
import {
  Article, Add, Edit, Archive, Publish, ContentCopy,
  Search, Print, Send, ExpandMore, ExpandLess, CheckCircle,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const CATEGORY_CFG: Record<string, { label: string; color: string; emoji: string }> = {
  employment:  { label: 'Employment Verification', color: '#1565c0', emoji: '📋' },
  salary:      { label: 'Salary Confirmation',     color: '#2e7d32', emoji: '💰' },
  noc:         { label: 'No Objection (NOC)',       color: '#6a1b9a', emoji: '✅' },
  offer:       { label: 'Offer Letter',             color: '#e65100', emoji: '🤝' },
  promotion:   { label: 'Promotion',                color: '#f57f17', emoji: '🎉' },
  warning:     { label: 'Warning Letter',           color: '#b71c1c', emoji: '⚠️' },
  termination: { label: 'Termination',              color: '#37474f', emoji: '🚪' },
  redundancy:  { label: 'Redundancy',               color: '#4e342e', emoji: '📉' },
  reference:   { label: 'Reference Letter',         color: '#00695c', emoji: '⭐' },
  visa_support:{ label: 'Visa Support',             color: '#0277bd', emoji: '✈️' },
  bank_support:{ label: 'Bank Support',             color: '#558b2f', emoji: '🏦' },
  loan_support:{ label: 'Loan / Mortgage',          color: '#4527a0', emoji: '🏠' },
  probation:   { label: 'Probation',                color: '#00838f', emoji: '🎓' },
  custom:      { label: 'Custom',                   color: '#78909c', emoji: '📄' },
};

interface LetterTemplate {
  id: string; name: string; description?: string; category: string;
  status: string; body: string; subject?: string;
  showLetterhead: boolean; showSignature: boolean; useCount: number;
  updatedAt: string;
}

interface GeneratedLetter {
  id: string; templateId: string; templateName?: string; renderedBody: string;
  addressedTo?: string; purpose?: string; refNumber?: string;
  letterDate?: string; isIssued: boolean; issuedAt?: string;
  createdAt: string;
  employee?: { firstName: string; lastName: string; department?: { name: string }; position?: { title: string } };
}

const emptyTplForm = {
  name: '', description: '', category: 'employment',
  body: '', showLetterhead: true, showSignature: true,
};

const DEFAULT_BODY = `<p style="text-align:right">{{letterDate}}</p>
<p>{{addressedTo}}</p>

<p><strong>Re: [Subject]</strong></p>

<p>To Whom It May Concern,</p>

<p>This letter concerns <strong>{{employeeFullName}}</strong> (Staff ID: {{employeeId}}), who is employed at <strong>{{companyName}}</strong> as <strong>{{position}}</strong> in the <strong>{{department}}</strong> department since <strong>{{startDate}}</strong>.</p>

<p>[Add your letter body here]</p>

<p>Yours faithfully,</p>`;

export default function LetterTemplates() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager'].includes((user as any)?.role || '');

  const [tab, setTab] = useState(0);
  const [templates, setTemplates] = useState<LetterTemplate[]>([]);
  const [generated, setGenerated] = useState<GeneratedLetter[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [mergeFields, setMergeFields] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Template form
  const [tplFormOpen, setTplFormOpen] = useState(false);
  const [tplForm, setTplForm] = useState({ ...emptyTplForm });
  const [editTplId, setEditTplId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showMergeFields, setShowMergeFields] = useState(false);

  // Generate dialog
  const [genDialogOpen, setGenDialogOpen] = useState(false);
  const [genTpl, setGenTpl] = useState<LetterTemplate | null>(null);
  const [genEmpId, setGenEmpId] = useState('');
  const [genAddressedTo, setGenAddressedTo] = useState('To Whom It May Concern');
  const [genPurpose, setGenPurpose] = useState('');
  const [genLetterDate, setGenLetterDate] = useState(new Date().toISOString().slice(0, 10));
  const [genResult, setGenResult] = useState<GeneratedLetter | null>(null);

  // Preview dialog
  const [previewLetter, setPreviewLetter] = useState<GeneratedLetter | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tab === 1) params.set('status', 'draft');
      else if (tab === 2) params.set('status', 'archived');

      const calls: Promise<any>[] = [
        apiFetch(`/api/letter-templates/templates?${params}`),
        apiFetch('/api/letter-templates/generated'),
        apiFetch('/api/letter-templates/merge-fields'),
        ...(isHR ? [apiFetch('/api/employees?limit=300')] : []),
      ];
      const results = await Promise.allSettled(calls);
      if (results[0].status === 'fulfilled') setTemplates(results[0].value ?? []);
      if (results[1].status === 'fulfilled') setGenerated(results[1].value ?? []);
      if (results[2].status === 'fulfilled') setMergeFields(results[2].value ?? {});
      if (results[3] && results[3].status === 'fulfilled') {
        const d = (results[3] as any).value;
        setEmployees(Array.isArray(d) ? d : (d.data ?? []));
      }
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [tab, isHR]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditTplId(null);
    setTplForm({ ...emptyTplForm, body: DEFAULT_BODY });
    setTplFormOpen(true);
  };

  const openEdit = (t: LetterTemplate) => {
    setEditTplId(t.id);
    setTplForm({ name: t.name, description: t.description || '', category: t.category, body: t.body, showLetterhead: t.showLetterhead, showSignature: t.showSignature });
    setTplFormOpen(true);
  };

  const saveTpl = async () => {
    setSaving(true);
    try {
      if (editTplId) {
        await apiFetch(`/api/letter-templates/templates/${editTplId}`, { method: 'PATCH', body: JSON.stringify(tplForm) });
      } else {
        await apiFetch('/api/letter-templates/templates', { method: 'POST', body: JSON.stringify(tplForm) });
      }
      setTplFormOpen(false);
      load();
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const publishTpl = async (id: string) => {
    await apiFetch(`/api/letter-templates/templates/${id}/publish`, { method: 'PATCH' });
    load();
  };
  const archiveTpl = async (id: string) => {
    await apiFetch(`/api/letter-templates/templates/${id}/archive`, { method: 'PATCH' });
    load();
  };

  const openGenerate = (tpl: LetterTemplate) => {
    setGenTpl(tpl); setGenEmpId(''); setGenAddressedTo('To Whom It May Concern');
    setGenPurpose(''); setGenLetterDate(new Date().toISOString().slice(0, 10));
    setGenResult(null); setGenDialogOpen(true);
  };

  const generate = async () => {
    if (!genTpl || !genEmpId) return;
    setSaving(true);
    try {
      const result = await apiFetch('/api/letter-templates/generate', {
        method: 'POST',
        body: JSON.stringify({ templateId: genTpl.id, employeeId: genEmpId, addressedTo: genAddressedTo, purpose: genPurpose, letterDate: genLetterDate }),
      });
      setGenResult(result);
      load();
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const issueLetter = async (id: string) => {
    await apiFetch(`/api/letter-templates/generated/${id}/issue`, { method: 'PATCH' });
    load();
    if (previewLetter?.id === id) setPreviewLetter(prev => prev ? { ...prev, isIssued: true } : prev);
  };

  const printLetter = (letter: GeneratedLetter, tplName?: string) => {
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>${letter.refNumber ?? 'Letter'}</title>
    <style>
      body { font-family: 'Times New Roman', serif; margin: 0; padding: 40px 60px; color: #1a1a1a; }
      .letterhead { text-align: center; border-bottom: 2px solid #C08A2E; padding-bottom: 16px; margin-bottom: 24px; }
      .company-name { font-size: 22px; font-weight: bold; color: #C08A2E; letter-spacing: 1px; }
      .company-addr { font-size: 11px; color: #555; margin-top: 4px; }
      .ref { font-size: 11px; color: #888; text-align: right; margin-bottom: 24px; }
      .body { line-height: 1.8; font-size: 13px; }
      .signature-block { margin-top: 48px; }
      .sig-line { border-top: 1px solid #333; width: 200px; margin-top: 40px; }
      p { margin: 0 0 12px; }
      @media print { body { padding: 20px 40px; } }
    </style></head><body>
    <div class="letterhead">
      <div class="company-name">Humanity Protocol</div>
      <div class="company-addr">8/F, The Loop, 33 Wellington Street, Central, Hong Kong</div>
    </div>
    <div class="ref">Ref: ${letter.refNumber ?? ''}</div>
    <div class="body">${letter.renderedBody}</div>
    <div class="signature-block">
      <div class="sig-line"></div>
      <p style="margin-top:8px"><strong>Authorised Signatory</strong><br>Human Resources<br>Humanity Protocol</p>
    </div>
    </body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 400);
  };

  const filteredTemplates = templates.filter(t => {
    if (catFilter && t.category !== catFilter) return false;
    if (search && !t.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const TABS = isHR ? ['Active', 'Drafts', 'Archived', 'Generated Letters'] : ['Templates', 'My Letters'];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#4e342e', width: 48, height: 48 }}><Article /></Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>HR Letter Templates</Typography>
            <Typography variant="body2" color="text.secondary">
              Generate employment letters, NOCs, references & more
            </Typography>
          </Box>
        </Box>
        {isHR && (
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}
            sx={{ bgcolor: '#4e342e', '&:hover': { bgcolor: '#3e2723' } }}>
            New Template
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        {TABS.map((t, i) => <Tab key={i} label={t} />)}
      </Tabs>

      {/* Template tabs (0,1,2) */}
      {(tab <= 2 || !isHR) && (
        <>
          <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField size="small" placeholder="Search templates…" value={search}
              onChange={e => setSearch(e.target.value)} sx={{ minWidth: 200 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
            <TextField select size="small" label="Category" value={catFilter}
              onChange={e => setCatFilter(e.target.value)} sx={{ minWidth: 200 }}>
              <MenuItem value="">All Categories</MenuItem>
              {Object.entries(CATEGORY_CFG).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v.emoji} {v.label}</MenuItem>
              ))}
            </TextField>
          </Paper>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
          ) : filteredTemplates.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
              <Article sx={{ fontSize: 64, opacity: 0.2, mb: 1 }} />
              <Typography>No templates found</Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {filteredTemplates.map(t => {
                const cfg = CATEGORY_CFG[t.category] ?? CATEGORY_CFG.custom;
                return (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={t.id}>
                    <Paper sx={{
                      p: 2, height: '100%', display: 'flex', flexDirection: 'column',
                      borderLeft: `4px solid ${cfg.color}`,
                      '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' }, transition: 'all 0.2s',
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="subtitle1" fontWeight={700}>
                          {cfg.emoji} {t.name}
                        </Typography>
                        {isHR && (
                          <Chip size="small" label={t.status}
                            sx={{ bgcolor: t.status === 'active' ? '#e8f5e9' : t.status === 'draft' ? '#fff8e1' : '#f5f5f5',
                              color: t.status === 'active' ? '#2e7d32' : t.status === 'draft' ? '#f57f17' : '#78909c',
                              fontSize: '0.65rem' }} />
                        )}
                      </Box>
                      <Chip size="small" label={cfg.label}
                        sx={{ alignSelf: 'flex-start', mb: 1, bgcolor: cfg.color + '15', color: cfg.color, fontSize: '0.65rem' }} />
                      {t.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, mb: 1,
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {t.description}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                        Used {t.useCount}× · Updated {new Date(t.updatedAt).toLocaleDateString()}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {isHR && (
                          <Button size="small" variant="contained" startIcon={<Send />}
                            onClick={() => openGenerate(t)}
                            sx={{ bgcolor: cfg.color, fontSize: '0.7rem', '&:hover': { bgcolor: cfg.color } }}>
                            Generate
                          </Button>
                        )}
                        {isHR && (
                          <>
                            <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(t)}><Edit fontSize="small" /></IconButton></Tooltip>
                            {t.status === 'draft' && (
                              <Tooltip title="Publish"><IconButton size="small" color="success" onClick={() => publishTpl(t.id)}><Publish fontSize="small" /></IconButton></Tooltip>
                            )}
                            {t.status === 'active' && (
                              <Tooltip title="Archive"><IconButton size="small" onClick={() => archiveTpl(t.id)}><Archive fontSize="small" /></IconButton></Tooltip>
                            )}
                          </>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </>
      )}

      {/* Generated letters tab */}
      {((isHR && tab === 3) || (!isHR && tab === 1)) && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ref</TableCell>
                {isHR && <TableCell>Employee</TableCell>}
                <TableCell>Template</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Purpose</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {generated.length === 0 && (
                <TableRow><TableCell colSpan={isHR ? 7 : 6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No letters generated yet
                </TableCell></TableRow>
              )}
              {generated.map(g => (
                <TableRow key={g.id} hover>
                  <TableCell><Typography variant="body2" fontWeight={600}>{g.refNumber}</Typography></TableCell>
                  {isHR && (
                    <TableCell>
                      <Typography variant="body2">{g.employee?.firstName} {g.employee?.lastName}</Typography>
                      <Typography variant="caption" color="text.secondary">{g.employee?.department?.name}</Typography>
                    </TableCell>
                  )}
                  <TableCell><Typography variant="body2">{g.templateName}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{g.letterDate}</Typography></TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary">{g.purpose || '—'}</Typography></TableCell>
                  <TableCell>
                    {g.isIssued
                      ? <Chip size="small" icon={<CheckCircle />} label="Issued" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontSize: '0.65rem' }} />
                      : <Chip size="small" label="Draft" sx={{ bgcolor: '#fff8e1', color: '#f57f17', fontSize: '0.65rem' }} />}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      <Tooltip title="Preview & Print">
                        <IconButton size="small" onClick={() => setPreviewLetter(g)}><Print fontSize="small" /></IconButton>
                      </Tooltip>
                      {isHR && !g.isIssued && (
                        <Tooltip title="Mark as Issued">
                          <IconButton size="small" color="success" onClick={() => issueLetter(g.id)}><CheckCircle fontSize="small" /></IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Template Create/Edit Dialog */}
      <Dialog open={tplFormOpen} onClose={() => setTplFormOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editTplId ? 'Edit Template' : 'New Letter Template'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth label="Template Name" value={tplForm.name}
              onChange={e => setTplForm(p => ({ ...p, name: e.target.value }))} required />
            <TextField select label="Category" value={tplForm.category}
              onChange={e => setTplForm(p => ({ ...p, category: e.target.value }))} sx={{ minWidth: 200 }}>
              {Object.entries(CATEGORY_CFG).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v.emoji} {v.label}</MenuItem>
              ))}
            </TextField>
          </Box>
          <TextField fullWidth label="Description" value={tplForm.description}
            onChange={e => setTplForm(p => ({ ...p, description: e.target.value }))} />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControlLabel control={<Switch checked={tplForm.showLetterhead}
              onChange={e => setTplForm(p => ({ ...p, showLetterhead: e.target.checked }))} />}
              label="Show Letterhead" />
            <FormControlLabel control={<Switch checked={tplForm.showSignature}
              onChange={e => setTplForm(p => ({ ...p, showSignature: e.target.checked }))} />}
              label="Show Signature Block" />
          </Box>

          {/* Merge fields reference */}
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => setShowMergeFields(p => !p)}>
              <Typography variant="subtitle2" fontWeight={700}>📌 Available Merge Fields (click to expand)</Typography>
              {showMergeFields ? <ExpandLess /> : <ExpandMore />}
            </Box>
            <Collapse in={showMergeFields}>
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {Object.entries(mergeFields).map(([k, v]) => (
                  <Tooltip key={k} title={v}>
                    <Chip size="small" label={k}
                      onClick={() => setTplForm(p => ({ ...p, body: p.body + k }))}
                      sx={{ fontFamily: 'monospace', fontSize: '0.65rem', cursor: 'pointer',
                        '&:hover': { bgcolor: '#e3f2fd' } }} />
                  </Tooltip>
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Click any field to insert it at the end of the body, or type it manually.
              </Typography>
            </Collapse>
          </Paper>

          <TextField fullWidth label="Letter Body (HTML supported)" multiline rows={12}
            value={tplForm.body} onChange={e => setTplForm(p => ({ ...p, body: e.target.value }))}
            sx={{ '& textarea': { fontFamily: 'monospace', fontSize: '0.82rem' } }}
            placeholder="Use {{employeeFullName}}, {{position}}, etc." />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTplFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveTpl} disabled={saving || !tplForm.name || !tplForm.body}
            sx={{ bgcolor: '#4e342e' }}>
            {saving ? 'Saving…' : editTplId ? 'Update' : 'Save Draft'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate Letter Dialog */}
      <Dialog open={genDialogOpen} onClose={() => { setGenDialogOpen(false); setGenResult(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          {genResult ? `✅ Letter Generated — ${genResult.refNumber}` : `Generate: ${genTpl?.name}`}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {!genResult ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField select fullWidth label="Employee" value={genEmpId}
                onChange={e => setGenEmpId(e.target.value)} required>
                {employees.map(e => (
                  <MenuItem key={e.id} value={e.id}>
                    {e.firstName} {e.lastName} — {e.department?.name} · {e.position?.title}
                  </MenuItem>
                ))}
              </TextField>
              <TextField fullWidth label="Addressed To" value={genAddressedTo}
                onChange={e => setGenAddressedTo(e.target.value)}
                placeholder="To Whom It May Concern" />
              <TextField fullWidth label="Purpose (for {{purpose}} field)" value={genPurpose}
                onChange={e => setGenPurpose(e.target.value)}
                placeholder="e.g. bank account opening, visa application" />
              <TextField fullWidth label="Letter Date" type="date" InputLabelProps={{ shrink: true }}
                value={genLetterDate} onChange={e => setGenLetterDate(e.target.value)} />
            </Box>
          ) : (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                Letter <strong>{genResult.refNumber}</strong> generated successfully.
              </Alert>
              <Typography variant="body2" gutterBottom>
                The letter has been generated with all merge fields resolved. You can now preview and print it.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <Button variant="contained" startIcon={<Print />} onClick={() => printLetter(genResult)}
                  sx={{ bgcolor: '#4e342e' }}>Preview & Print</Button>
                <Button variant="outlined" startIcon={<CheckCircle />} onClick={() => issueLetter(genResult.id)}
                  color="success">Mark as Issued</Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        {!genResult && (
          <DialogActions>
            <Button onClick={() => setGenDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={generate}
              disabled={saving || !genEmpId}
              sx={{ bgcolor: '#4e342e' }}>
              {saving ? 'Generating…' : 'Generate Letter'}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewLetter} onClose={() => setPreviewLetter(null)} maxWidth="md" fullWidth>
        {previewLetter && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography fontWeight={700}>{previewLetter.refNumber} — {previewLetter.templateName}</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button startIcon={<Print />} variant="contained"
                    onClick={() => printLetter(previewLetter)} sx={{ bgcolor: '#4e342e' }}>
                    Print / Save PDF
                  </Button>
                  {isHR && !previewLetter.isIssued && (
                    <Button startIcon={<CheckCircle />} variant="outlined" color="success"
                      onClick={() => issueLetter(previewLetter.id)}>
                      Mark Issued
                    </Button>
                  )}
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              {/* Letterhead preview */}
              <Box sx={{ borderBottom: '2px solid #C08A2E', pb: 2, mb: 3, textAlign: 'center' }}>
                <Typography variant="h6" fontWeight={700} color="#C08A2E">Humanity Protocol</Typography>
                <Typography variant="caption" color="text.secondary">8/F, The Loop, 33 Wellington Street, Central, Hong Kong</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mb: 2 }}>
                Ref: {previewLetter.refNumber}
              </Typography>
              <Box sx={{ fontFamily: '"Times New Roman", serif', lineHeight: 1.8, fontSize: '0.9rem' }}
                dangerouslySetInnerHTML={{ __html: previewLetter.renderedBody }} />
              <Box sx={{ mt: 6, borderTop: '1px solid #333', pt: 1, width: 200 }}>
                <Typography variant="body2"><strong>Authorised Signatory</strong></Typography>
                <Typography variant="body2">Human Resources</Typography>
                <Typography variant="body2">Humanity Protocol</Typography>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
}
